import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface CallHistoryRecord {
  id: number;
  companyId: number;
  phoneNumber: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  sentAt: string;
  cost: number;
  errorMessage?: string;
}

interface FilterOptions {
  startDate: string;
  endDate: string;
  status: string;
  phoneNumber: string;
}

const CallHistory: React.FC = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState<CallHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: '',
    phoneNumber: ''
  });
  const [selectedRecord, setSelectedRecord] = useState<CallHistoryRecord | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (user) {
      loadCallHistory();
    }
  }, [user]);

  const loadCallHistory = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.phoneNumber) params.append('phoneNumber', filters.phoneNumber);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/call-history?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // API는 배열을 직접 반환
      if (Array.isArray(response.data)) {
        setRecords(response.data);
      } else {
        setRecords([]);
      }
    } catch (error: any) {
      console.error('Failed to load call history:', error);
      const errorMessage = error.response?.data?.message || error.message || '발송 내역을 불러오는데 실패했습니다.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = () => {
    loadCallHistory();
  };

  const handleReset = () => {
    setFilters({
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      status: '',
      phoneNumber: ''
    });
  };

  const handleExportCSV = () => {
    if (records.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = ['발송일시', '전화번호', '메시지', '상태', '비용', '오류메시지'];
    const csvContent = [
      headers.join(','),
      ...records.map(record => {
        return [
          new Date(record.sentAt).toLocaleString('ko-KR'),
          record.phoneNumber,
          `"${record.message.replace(/"/g, '""')}"`,
          record.status === 'success' ? '성공' : record.status === 'failed' ? '실패' : '대기중',
          record.cost,
          record.errorMessage ? `"${record.errorMessage.replace(/"/g, '""')}"` : ''
        ].join(',');
      })
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `call_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDetail = (record: CallHistoryRecord) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="status-badge success">성공</span>;
      case 'failed':
        return <span className="status-badge failed">실패</span>;
      case 'pending':
        return <span className="status-badge pending">대기중</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const getTotalStats = () => {
    const total = records.length;
    const success = records.filter(r => r.status === 'success').length;
    const failed = records.filter(r => r.status === 'failed').length;
    const totalCost = records.filter(r => r.status === 'success').reduce((sum, r) => sum + r.cost, 0);

    return { total, success, failed, totalCost };
  };

  const stats = getTotalStats();

  return (
    <div className="call-history-page">
      <section className="call-history-content section">
        <div className="container-wide">
          <div className="card">
            <h2 className="card-title">발송 내역 조회</h2>

            {/* 통계 요약 */}
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-label">전체 발송</span>
                <span className="stat-value">{stats.total}건</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">성공</span>
                <span className="stat-value">{stats.success}건</span>
              </div>
              <div className="stat-item failed">
                <span className="stat-label">실패</span>
                <span className="stat-value">{stats.failed}건</span>
              </div>
              <div className="stat-item cost">
                <span className="stat-label">총 비용</span>
                <span className="stat-value">{stats.totalCost.toLocaleString()}원</span>
              </div>
            </div>

            {/* 필터 */}
            <div className="filter-section">
              <div className="filter-row">
                <div className="filter-group">
                  <label className="filter-label">시작일</label>
                  <input
                    type="date"
                    name="startDate"
                    className="form-input"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">종료일</label>
                  <input
                    type="date"
                    name="endDate"
                    className="form-input"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">상태</label>
                  <select
                    name="status"
                    className="form-select"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">전체</option>
                    <option value="success">성공</option>
                    <option value="failed">실패</option>
                    <option value="pending">대기중</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">전화번호</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    className="form-input"
                    placeholder="010-0000-0000"
                    value={filters.phoneNumber}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
              <div className="filter-actions">
                <button className="btn btn-primary" onClick={handleSearch}>
                  조회
                </button>
                <button className="btn btn-secondary" onClick={handleReset}>
                  초기화
                </button>
                <button className="btn btn-secondary" onClick={handleExportCSV}>
                  CSV 다운로드
                </button>
              </div>
            </div>

            {/* 테이블 */}
            <div className="table-container">
              {loading ? (
                <div className="loading-state">
                  <p>데이터를 불러오는 중...</p>
                </div>
              ) : records.length === 0 ? (
                <div className="empty-state">
                  <p>조회된 발송 내역이 없습니다.</p>
                </div>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>발송일시</th>
                      <th>전화번호</th>
                      <th>메시지</th>
                      <th>상태</th>
                      <th>비용</th>
                      <th>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(record => (
                      <tr key={record.id}>
                        <td>{new Date(record.sentAt).toLocaleString('ko-KR')}</td>
                        <td className="phone-number">{record.phoneNumber}</td>
                        <td className="message-cell">
                          {record.message.length > 30
                            ? `${record.message.substring(0, 30)}...`
                            : record.message}
                        </td>
                        <td>{getStatusBadge(record.status)}</td>
                        <td className="cost-cell">{record.cost.toLocaleString()}원</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleViewDetail(record)}
                          >
                            상세보기
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 상세 모달 */}
      {showDetailModal && selectedRecord && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>발송 상세 내역</h2>
              <button
                className="modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">발송 ID</span>
                  <span className="detail-value">{selectedRecord.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">발송일시</span>
                  <span className="detail-value">
                    {new Date(selectedRecord.sentAt).toLocaleString('ko-KR')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">전화번호</span>
                  <span className="detail-value phone-number">{selectedRecord.phoneNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">상태</span>
                  <span className="detail-value">{getStatusBadge(selectedRecord.status)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">비용</span>
                  <span className="detail-value">{selectedRecord.cost.toLocaleString()}원</span>
                </div>
                <div className="detail-row full-width">
                  <span className="detail-label">메시지</span>
                  <div className="detail-message">{selectedRecord.message}</div>
                </div>
                {selectedRecord.errorMessage && (
                  <div className="detail-row full-width error">
                    <span className="detail-label">오류 메시지</span>
                    <div className="detail-error">{selectedRecord.errorMessage}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDetailModal(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallHistory;
